import { z } from "zod";

// Typed, fail-fast app configuration. Only public values (VITE_*) reach the browser.
const envSchema = z.object({
  VITE_FUNCTIONS_URL: z.string().url(),
  // Supabase project URL for social OAuth. Optional: derived from the functions
  // URL when unset (same host on supabase.co).
  VITE_SUPABASE_URL: z.string().url().optional(),
  VITE_SUPABASE_ANON_KEY: z.string().min(1),
  // Public Cloudflare Turnstile site key. Optional: when unset, the widget is
  // skipped (e.g. local dev) and the server does not enforce the captcha.
  VITE_TURNSTILE_SITE_KEY: z.string().optional(),
});

export type AppEnv = z.infer<typeof envSchema>;

// Serve the PRODUCTION project through its branded custom domain so users never see the raw
// *.supabase.co host (trust + less confusion — every endpoint we call or display is
// api.recruittrucker.com). Only the known prod host is aliased; DEV/local hosts are left
// untouched so DEV + e2e keep hitting the dev project. The custom domain is a Supabase Custom
// Domain that proxies the whole project (functions/auth/storage).
const HOST_ALIASES: Record<string, string> = {
  "exdmcnxoqbszoxjsgfkw.supabase.co": "api.recruittrucker.com",
};

/** Rewrite a known raw-Supabase host to its branded custom domain; other URLs pass through. */
export function brandUrl(url: string): string {
  try {
    const u = new URL(url);
    const alias = HOST_ALIASES[u.host];
    if (alias) u.host = alias;
    return u.toString().replace(/\/$/, "");
  } catch {
    return url;
  }
}

/** Validate a config source; throws a clear error on missing/invalid values. */
export function parseEnv(source: Record<string, unknown>): AppEnv {
  const result = envSchema.safeParse(source);
  if (!result.success) {
    const detail = result.error.issues
      .map((i) => `${i.path.join(".")}: ${i.message}`)
      .join("; ");
    throw new Error(`Invalid app configuration — ${detail}`);
  }
  const data = result.data;
  return {
    ...data,
    VITE_FUNCTIONS_URL: brandUrl(data.VITE_FUNCTIONS_URL),
    VITE_SUPABASE_URL: data.VITE_SUPABASE_URL ? brandUrl(data.VITE_SUPABASE_URL) : data.VITE_SUPABASE_URL,
  };
}

let cached: AppEnv | null = null;

/** The validated environment. Fails fast the first time it is read. */
export function getEnv(): AppEnv {
  if (!cached) cached = parseEnv(import.meta.env as unknown as Record<string, unknown>);
  return cached;
}
